// quiz-recipe-logic.js
(function initQuizRecipeLogic() {
  window.CatQuiz = window.CatQuiz || {};

  const state = {
    detailsById: new Map(),
    hydrationPromise: null
  };

  function getRoot() {
    return document.getElementById("cat-nutrition-quiz");
  }

  function getForm(root = getRoot()) {
    return root?.querySelector?.("#cat-quiz-form") || null;
  }

  function normalizeFlow(value) {
    return String(value || "").trim().toLowerCase();
  }

  function getCurrentFlow(form = getForm()) {
    const checked = form?.querySelector?.('input[name="food_choice"]:checked')?.value;
    return normalizeFlow(checked);
  }

  function isStepUpsellEligible(flow) {
    return flow === "fellinora" || flow === "other";
  }

  function isCookResultEligible(flow) {
    return flow === "cook";
  }

  function isBarfResultEligible(flow) {
    return flow === "barf";
  }

  function ensureSelectionField(form = getForm()) {
    if (!form) return null;
    let field = form.querySelector('input[name="upsell_recipe_variant_ids"]');
    if (!field) {
      field = document.createElement("input");
      field.type = "hidden";
      field.name = "upsell_recipe_variant_ids";
      field.value = "";
      form.appendChild(field);
    }
    return field;
  }

  function readSelectedIds(form = getForm()) {
    const field = ensureSelectionField(form);
    const raw = (field?.value || "").trim();
    if (!raw) return [];
    return raw
      .split(",")
      .map((v) => Number(String(v).trim()))
      .filter((v) => Number.isFinite(v) && v > 0);
  }

  function writeSelectedIds(ids, form = getForm()) {
    const field = ensureSelectionField(form);
    if (!field) return;

    const unique = Array.from(new Set(
      (Array.isArray(ids) ? ids : [])
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v) && v > 0)
    ));

    field.value = unique.join(",");
  }

  function toggleSelectedId(variantId, form = getForm()) {
    const current = new Set(readSelectedIds(form));
    if (current.has(variantId)) current.delete(variantId);
    else current.add(variantId);
    writeSelectedIds(Array.from(current), form);
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function formatPrice(cents) {
    if (typeof window.formatEURFromCents === "function") {
      return window.formatEURFromCents(cents);
    }
    if (!Number.isFinite(cents)) return "–";
    return `${(cents / 100).toFixed(2).replace(".", ",")} €`;
  }

  function normalizeKey(value) {
    return String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  function inferRecipeMeta(rawTitle) {
    const title = normalizeKey(rawTitle);

    if (/(forelle|fisch|lachs|thunfisch|makrele|seelachs)/.test(title)) {
      return {
        proteinKey: "fisch",
        groupKey: "fish",
        groupLabel: "Fisch",
        sortOrder: 10
      };
    }

    if (/huhn/.test(title)) {
      return {
        proteinKey: "huhn",
        groupKey: "meat",
        groupLabel: "Fleisch",
        sortOrder: 20
      };
    }

    if (/wild/.test(title)) {
      return {
        proteinKey: "wild",
        groupKey: "meat",
        groupLabel: "Fleisch",
        sortOrder: 30
      };
    }

    if (/rind/.test(title)) {
      return {
        proteinKey: "rind",
        groupKey: "meat",
        groupLabel: "Fleisch",
        sortOrder: 40
      };
    }

    if (/pute/.test(title)) {
      return {
        proteinKey: "pute",
        groupKey: "meat",
        groupLabel: "Fleisch",
        sortOrder: 50
      };
    }

    if (/kaninchen/.test(title)) {
      return {
        proteinKey: "kaninchen",
        groupKey: "meat",
        groupLabel: "Fleisch",
        sortOrder: 60
      };
    }

    if (/lamm/.test(title)) {
      return {
        proteinKey: "lamm",
        groupKey: "meat",
        groupLabel: "Fleisch",
        sortOrder: 70
      };
    }

    if (/ente/.test(title)) {
      return {
        proteinKey: "ente",
        groupKey: "meat",
        groupLabel: "Fleisch",
        sortOrder: 80
      };
    }

    return {
      proteinKey: "other",
      groupKey: "meat",
      groupLabel: "Fleisch",
      sortOrder: 999
    };
  }

  function getCatalogFromConfig(configKey) {
    const source = Array.isArray(CatQuiz.config?.[configKey])
      ? CatQuiz.config[configKey]
      : [];

    return source
      .map((item) => {
        const variantId = Number(item?.variantId);
        if (!(Number.isFinite(variantId) && variantId > 0)) return null;

        const fallbackTitle = String(item?.fallbackTitle || "Rezept").trim() || "Rezept";
        const meta = inferRecipeMeta(fallbackTitle);

        return {
          variantId,
          fallbackTitle,
          ingredients: Array.isArray(item?.ingredients) ? item.ingredients : [],
          ...meta
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.variantId - b.variantId);
  }

  function getCatalog() {
    return getCatalogFromConfig("RECIPE_UPSELL_VARIANTS");
  }

  function getBarfCatalog() {
    return getCatalogFromConfig("BARF_RECIPE_VARIANTS");
  }

  async function fetchVariantDetails(variantId) {
    const res = await fetch(`/variants/${variantId}.js?ts=${Date.now()}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store"
    });

    if (!res.ok) {
      throw new Error(`Variant details fetch failed (${variantId}): ${res.status}`);
    }

    const json = await res.json();
    const priceRaw = json?.price;
    const priceCents = typeof priceRaw === "number" ? priceRaw : parseInt(String(priceRaw), 10);

    return {
      variantId,
      title: json?.product_title || json?.name || json?.title || `Rezept ${variantId}`,
      priceCents: Number.isFinite(priceCents) ? priceCents : null,
      available: json?.available !== false
    };
  }

  function recipeDetails(recipe) {
    return state.detailsById.get(Number(recipe?.variantId)) || {
      variantId: Number(recipe?.variantId),
      title: recipe?.fallbackTitle || "Rezept",
      priceCents: null,
      available: true
    };
  }

  function hydrateCatalog() {
    if (state.hydrationPromise) return state.hydrationPromise;

    const catalog = Array.from(new Map(
      [...getCatalog(), ...getBarfCatalog()].map((item) => [Number(item.variantId), item])
    ).values());

    if (!catalog.length) {
      state.hydrationPromise = Promise.resolve(true);
      return state.hydrationPromise;
    }

    state.hydrationPromise = Promise.allSettled(
      catalog.map(async (recipe) => {
        try {
          const details = await fetchVariantDetails(recipe.variantId);
          state.detailsById.set(recipe.variantId, details);
        } catch (err) {
          state.detailsById.set(recipe.variantId, {
            variantId: recipe.variantId,
            title: recipe.fallbackTitle,
            priceCents: null,
            available: true,
            error: true
          });
        }
      })
    ).then(() => true);

    return state.hydrationPromise;
  }

  function normalizeAllergySet(answers = {}) {
    const raw = Array.isArray(answers?.allergyProteins) ? answers.allergyProteins : [];
    return new Set(raw.map((item) => normalizeKey(item)));
  }

  function filterRecipesByAllergies(recipes, answers = {}) {
    const allergies = normalizeAllergySet(answers);
    const catalog = Array.isArray(recipes) ? recipes : [];

    return catalog.filter((recipe) => {
      if (recipe.groupKey === "fish" && allergies.has("fisch")) return false;
      if (recipe.proteinKey && allergies.has(recipe.proteinKey)) return false;
      return true;
    });
  }

  function groupRecipes(recipes) {
    const list = Array.isArray(recipes) ? recipes : [];
    return {
      fish: list.filter((recipe) => recipe.groupKey === "fish"),
      meat: list.filter((recipe) => recipe.groupKey !== "fish")
    };
  }

  function getCookFilteredRecipes(answers = {}) {
    return filterRecipesByAllergies(getCatalog(), answers);
  }

  function getCookGroupedRecipes(answers = {}) {
    return groupRecipes(getCookFilteredRecipes(answers));
  }

  function getBarfFilteredRecipes(answers = {}) {
    return filterRecipesByAllergies(getBarfCatalog(), answers);
  }

  function getBarfGroupedRecipes(answers = {}) {
    return groupRecipes(getBarfFilteredRecipes(answers));
  }

  function syncSelectedIdsToAllowedRecipes(allowedRecipes, form = getForm()) {
    const allowed = new Set((Array.isArray(allowedRecipes) ? allowedRecipes : []).map((item) => Number(item.variantId)));
    const nextIds = readSelectedIds(form).filter((variantId) => allowed.has(Number(variantId)));
    writeSelectedIds(nextIds, form);
    return nextIds;
  }

  function buildCartItems(context = {}) {
    const form = context.form || getForm();
    const ids = readSelectedIds(form);
    return ids.map((variantId) => ({
      id: variantId,
      quantity: 1,
      properties: {
        "Plan-Typ": "Rezept"
      }
    }));
  }

  const api = {
    getRoot,
    getForm,
    normalizeFlow,
    getCurrentFlow,
    isStepUpsellEligible,
    isCookResultEligible,
    isBarfResultEligible,
    ensureSelectionField,
    readSelectedIds,
    writeSelectedIds,
    toggleSelectedId,
    escapeHtml,
    formatPrice,
    getCatalog,
    getBarfCatalog,
    hydrateCatalog,
    recipeDetails,
    getCookFilteredRecipes,
    getCookGroupedRecipes,
    getBarfFilteredRecipes,
    getBarfGroupedRecipes,
    syncSelectedIdsToAllowedRecipes,
    buildCartItems
  };

  window.CatQuizRecipeLogic = api;
  CatQuiz.recipeLogic = api;
})();
